import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { cloneDeep, debounce } from 'lodash'
import { fromZonedTime } from 'date-fns-tz'
import { startOfDay } from 'date-fns'
import { useTranslation } from 'react-i18next'

import ModalContainer from '../../../ModalContainer/ModelContainer'
import SearchField from '../../../FormLegacy/FormFieldsV2/SearchField'
import Filter from '../../../Filter/Filter'
import FeaturedTable from '../../../FeaturedTable/FeaturedTable'
import ConfirmPopup from '../../../Popups/ConfirmPopup'
import RadioField from '../../../FormLegacy/FormFieldsV2/RadioField'
import DateField from '../../../FormLegacy/FormFieldsV2/DateField'
import Icon from '../../../Icons'
import RewardsAddLoyaltyActivityModal from '../RewardsAddLoyaltyActivityModal/RewardsAddLoyaltyActivityModal'
import RewardsEditActivityModal from '../RewardsEditActivityModal/RewardsEditActivityModal'
import RewardsActivityDetailsModal from '../RewardsActivityDetailsModal/RewardsActivityDetailsModal'
import {
  fetchBuildingActivities,
  selectBuildingActivities,
  updateBuildingActivityStatus,
  deleteBuildingActivity,
} from '../../../../reducers/loyaltyReducer'
import { getActivityType, manageActivitiesColumns } from '../helpers'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'

const ACTIVITIES_STRUCTURE = {
  Featured: [],
  'Custom Activities': [],
  'Reservations & Bookings': [],
  'Community Engagement': [],
  'Administrative & Feedback': [],
}

// `ACTIVITIES_STRUCTURE`'s keys are raw English category values matched against
// `activity.category` from the API — never translate them. Only translate the display
// label rendered via this lookup.
const ACTIVITY_CATEGORY_LABEL_KEYS = {
  Featured: 'rewards-loyalty:manageActivities.categories.featured',
  'Custom Activities': 'rewards-loyalty:manageActivities.categories.customActivities',
  'Reservations & Bookings': 'rewards-loyalty:manageActivities.categories.reservationsAndBookings',
  'Community Engagement': 'rewards-loyalty:manageActivities.categories.communityEngagement',
  'Administrative & Feedback':
    'rewards-loyalty:manageActivities.categories.administrativeAndFeedback',
}

const RewardsManageActivitiesModal = ({
  onClose = () => {},
  fetchBuildingActivities = () => {},
  updateBuildingActivityStatus = () => {},
  deleteBuildingActivity = () => {},
  activities = [],
  building,
}) => {
  const { t } = useTranslation(['rewards-loyalty', 'common'])
  const activityType = getActivityType(t)
  const { timezone } = building
  const [isLoading, setIsLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState(activityType[0])
  const [featuredActivity, setFeaturedActivity] = useState(null)
  const [unfeatureActivity, setUnfeatureActivity] = useState(null)
  const [featureMethod, setFeatureMethod] = useState('auto')
  const [expirationDate, setExpirationDate] = useState(null)
  const [expirationError, setExpirationError] = useState(false)
  const [activateActivity, setActivateActivity] = useState(null)
  const [deleteActivity, setDeleteActivity] = useState(null)
  const [showAddActivity, setShowAddActivity] = useState(false)
  const [editActivity, setEditActivity] = useState(null)
  const [detailsActivity, setDetailsActivity] = useState(null)
  const { displaySnackbar } = useSnackbar()
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(search)

  useEffect(() => {
    const debouncedSetter = debounce(setDebouncedSearchValue, 250)
    debouncedSetter(search)

    return () => {
      debouncedSetter.cancel()
    }
  }, [search])

  const mutateActivities = (dataToMutate = []) => {
    const mutated = cloneDeep(ACTIVITIES_STRUCTURE)
    dataToMutate.forEach((activity) =>
      activity.isFeatured
        ? mutated['Featured'].push(activity)
        : mutated[activity.category].push(activity),
    )
    return mutated
  }

  const fetchActivities = async () => {
    try {
      setIsLoading(true)
      await fetchBuildingActivities(building.id)
    } catch ({ message }) {
      console.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const onHandleSearch = useCallback((e) => setSearch(e.target.value), [])

  const data = useMemo(() => {
    const filtered = activities.filter((activity) => {
      const matchesStatus =
        statusFilter.value === 'all' ||
        (statusFilter.value === 'active' && activity.isActive) ||
        (statusFilter.value === 'inactive' && !activity.isActive)

      const matchesSearch =
        !debouncedSearchValue.length ||
        activity.name.toLowerCase().includes(debouncedSearchValue.toLowerCase())

      return matchesStatus && matchesSearch
    })
    return mutateActivities(filtered)
  }, [activities, debouncedSearchValue, statusFilter])

  const onMakeFeatured = async () => {
    if (featureMethod === 'auto' && !expirationDate) {
      setExpirationError(true)
    } else {
      const data = { isFeatured: true }

      if (expirationDate) {
        data.expirationDate = fromZonedTime(startOfDay(expirationDate), timezone).getTime()
      }

      try {
        await updateBuildingActivityStatus(featuredActivity.id, data)
      } catch ({ message }) {
        console.error(message)
      } finally {
        setFeaturedActivity(null)
        setExpirationDate(null)
      }
    }
  }

  const onSwitchStatus = async (activityId, status) => {
    try {
      await updateBuildingActivityStatus(activityId, { isActive: status })
    } catch ({ message }) {
      console.error(message)
    }
  }

  const onActivate = async () => {
    try {
      const data = { isActive: true }

      if (expirationDate) {
        data.expirationDate = fromZonedTime(startOfDay(expirationDate), timezone).getTime()
      }

      await updateBuildingActivityStatus(activateActivity.id, data)
    } catch ({ message }) {
      console.error(message)
    } finally {
      setActivateActivity(null)
      setExpirationDate(null)
    }
  }

  const onUnfetureActivity = async () => {
    try {
      const data = { isFeatured: false }
      await updateBuildingActivityStatus(unfeatureActivity.id, data)
    } catch ({ message }) {
      console.error(message)
    } finally {
      setUnfeatureActivity(null)
    }
  }

  const cautionFeaturedBody = () => {
    const { name } = featuredActivity

    return (
      <div className="caution-activity-body">
        <span>{t('rewards-loyalty:manageActivities.featureActivityConfirm', { name })}</span>
        <div className="caution-activity-featured">
          <div className="radio-group-row">
            <RadioField
              type="radio"
              name="featureMethod"
              label={t('rewards-loyalty:manageActivities.setExpirationDateLabel')}
              value={'auto'}
              onChange={(e) => setFeatureMethod(e.target.value)}
              checked={featureMethod === 'auto'}
            />
            <RadioField
              type="radio"
              name="featureMethod"
              label={t('rewards-loyalty:manageActivities.unfeatureManuallyLabel')}
              value={'manually'}
              onChange={(e) => {
                setFeatureMethod(e.target.value)
                setExpirationDate(null)
                setExpirationError(false)
              }}
              checked={featureMethod === 'manually'}
            />
          </div>
          <DateField
            label={t('rewards-loyalty:manageActivities.pinnedActivityExpirationDateLabel')}
            name="expirationDate"
            placeholderText="(MM/DD/YYYY)"
            autoComplete="off"
            dateFormat="MM/dd/yyyy"
            minDate={new Date()}
            value={expirationDate}
            onChange={(e) => {
              setExpirationDate(new Date(e.target.value).getTime())
              setExpirationError(false)
            }}
            onChangeRaw={(e) => e.preventDefault()}
            disabled={featureMethod === 'manually'}
            isError={expirationError}
            errorLabel={t('rewards-loyalty:manageActivities.selectExpirationDateError')}
          />
        </div>
      </div>
    )
  }

  const cautionActivateBody = () => (
    <div className="caution-activity-body">
      <span>{t('rewards-loyalty:manageActivities.activateExpiredActivityInfo')}</span>
      <div className="caution-activity-featured">
        <DateField
          label={
            <React.Fragment>
              {t('rewards-loyalty:activityForm.expirationDateLabel')}{' '}
              <span>({t('rewards-loyalty:activityForm.optionalSuffix')})</span>
            </React.Fragment>
          }
          name="expirationDate"
          placeholderText="(MM/DD/YYYY)"
          autoComplete="off"
          dateFormat="MM/dd/yyyy"
          minDate={new Date()}
          value={expirationDate}
          onChange={(e) => {
            setExpirationDate(new Date(e.target.value).getTime())
            setExpirationError(false)
          }}
          onChangeRaw={(e) => e.preventDefault()}
          isError={expirationError}
          errorLabel={t('rewards-loyalty:manageActivities.selectExpirationDateError')}
        />
      </div>
    </div>
  )

  const onDeleteActivity = async () => {
    try {
      setIsLoading(true)
      await deleteBuildingActivity(deleteActivity.id)
      displaySnackbar(
        t('rewards-loyalty:manageActivities.deleteActivitySuccess', {
          name: deleteActivity.name,
        }),
      )
    } catch ({ message }) {
      console.error(message)
      displaySnackbar(t('rewards-loyalty:manageActivities.deleteActivityError'), true)
    } finally {
      setDeleteActivity(null)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (building) {
      fetchActivities()
    }
  }, [building])

  return (
    <ModalContainer
      additionalClass={'rewards-modal'}
      onClose={onClose}
      headerTitle={t('rewards-loyalty:manageActivities.headerTitle')}
      headerIcon={<Icon icon="walk" />}
    >
      <div
        className={`rewards-modal-content rewards-base-view loyalty-manage-activities ${
          isLoading ? 'is-loading' : ''
        }`}
      >
        <div className="loyalty-manage-activities-head">
          <div>
            <SearchField
              placeholder={t('rewards-loyalty:manageActivities.searchByActivityNamePlaceholder')}
              value={search}
              onChange={onHandleSearch}
            />
            <Filter
              placeholder={
                statusFilter
                  ? activityType.find((x) => x.id === statusFilter.id).text
                  : t('rewards-loyalty:manageActivities.selectStatusPlaceholder')
              }
              options={activityType}
              value={statusFilter ? [statusFilter] : []}
              name={'ActivityType'}
              onChange={(e) => setStatusFilter(e.target.value)}
              closeAfterSelect
            />
          </div>
          <div>
            <button
              className="button button-black"
              onClick={() => setShowAddActivity(true)}
              disabled={isLoading}
            >
              {t('rewards-loyalty:loyaltyTable.addActivityButton')}
            </button>
          </div>
        </div>
        <div className="loyalty-manage-activities-body">
          {data &&
            Object.keys(data).map(
              (key) =>
                data[key] &&
                data[key].length > 0 && (
                  <div className="loyalty-manage-activities-content" key={key}>
                    <span>{t(ACTIVITY_CATEGORY_LABEL_KEYS[key])}</span>
                    <div className="rewards-table" style={{ padding: 0 }}>
                      <div className={`ctp-rewards-loyalty-list`}>
                        <FeaturedTable
                          columns={manageActivitiesColumns({
                            timezone,
                            onSwitch: onSwitchStatus,
                            onMakeFeatured: setFeaturedActivity,
                            onActivate: setActivateActivity,
                            onDelete: setDeleteActivity,
                            onEdit: setEditActivity,
                            onDetails: setDetailsActivity,
                            onMakeUnfeatured: setUnfeatureActivity,
                            t,
                          })}
                          data={data[key]}
                        />
                      </div>
                    </div>
                  </div>
                ),
            )}
        </div>
      </div>
      {featuredActivity && (
        <ConfirmPopup
          title={t('rewards-loyalty:loyaltyTable.cautionTitle')}
          cancelText={t('rewards-loyalty:loyaltyTable.closeButton')}
          confirmText={t('rewards-loyalty:manageActivities.makeFeaturedButton')}
          isOpen={!!featuredActivity}
          body={cautionFeaturedBody()}
          cancelCallback={() => setFeaturedActivity(null)}
          confirmCallback={onMakeFeatured}
          className={'is-caution'}
        />
      )}
      {activateActivity && (
        <ConfirmPopup
          title={t('rewards-loyalty:loyaltyTable.cautionTitle')}
          cancelText={t('rewards-loyalty:loyaltyTable.closeButton')}
          confirmText={t('rewards-loyalty:manageActivitiesColumns.activateOption')}
          isOpen={!!activateActivity}
          body={cautionActivateBody()}
          cancelCallback={() => setActivateActivity(null)}
          confirmCallback={onActivate}
          className={'is-caution'}
        />
      )}
      {deleteActivity && (
        <ConfirmPopup
          title={t('rewards-loyalty:loyaltyTable.confirmationTitle')}
          cancelText={t('common:actions.cancel')}
          confirmText={t('rewards-loyalty:manageActivities.yesDeleteActivityButton')}
          isOpen={!!deleteActivity}
          body={t('rewards-loyalty:manageActivities.deleteActivityConfirm', {
            name: deleteActivity.name,
          })}
          cancelCallback={() => setDeleteActivity(null)}
          confirmCallback={onDeleteActivity}
        />
      )}
      {showAddActivity && (
        <RewardsAddLoyaltyActivityModal
          building={building}
          onClose={() => setShowAddActivity(false)}
        />
      )}
      {editActivity && (
        <RewardsEditActivityModal activity={editActivity} onClose={() => setEditActivity(null)} />
      )}
      {detailsActivity && (
        <RewardsActivityDetailsModal
          building={building}
          onClose={() => setDetailsActivity(null)}
          activity={detailsActivity}
        />
      )}
      {unfeatureActivity && (
        <ConfirmPopup
          title={t('rewards-loyalty:loyaltyTable.confirmationTitle')}
          cancelText={t('common:actions.cancel')}
          confirmText={t('rewards-loyalty:manageActivities.yesUnfeatureActivityButton')}
          isOpen={!!unfeatureActivity}
          body={t('rewards-loyalty:manageActivities.unfeatureActivityConfirm', {
            name: unfeatureActivity.name,
          })}
          cancelCallback={() => setUnfeatureActivity(null)}
          confirmCallback={onUnfetureActivity}
        />
      )}
    </ModalContainer>
  )
}

RewardsManageActivitiesModal.propTypes = {
  onClose: PropTypes.func,
  fetchBuildingActivities: PropTypes.func,
  updateBuildingActivityStatus: PropTypes.func,
  deleteBuildingActivity: PropTypes.func,
  building: PropTypes.object,
  activities: PropTypes.array,
}

export default connect((state) => ({ activities: selectBuildingActivities(state) }), {
  fetchBuildingActivities,
  updateBuildingActivityStatus,
  deleteBuildingActivity,
})(RewardsManageActivitiesModal)
